import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, ArrowRightLeft, Globe, ShieldCheck } from 'lucide-react'; // Added ShieldCheck icon
import { db } from '../firebaseConfig'; // Import your firebase config
import { doc, getDoc } from 'firebase/firestore';
import '../App.css';
import Navbar from '../componenets/Navbar';
import Footer from '../componenets/Footer';

interface Rates {
  [key: string]: number;
}

const ExchangeRatePage: React.FC = () => {
  const [rates, setRates] = useState<Rates>({});
  const [adminLkrRate, setAdminLkrRate] = useState<number | null>(null); // New state for admin rate
  const [loading, setLoading] = useState<boolean>(true);
  const [baseCurrency, setBaseCurrency] = useState<string>('USD');
  const [amount, setAmount] = useState<number>(1);
  const [lastUpdated, setLastUpdated] = useState<string>('');

  const targetCurrencies: string[] = [
    'USD', 'EUR', 'GBP', 'AED', 'JPY', 'CNY', 'LKR', 'INR', 'AUD', 'CAD'
  ];

  const currencyNames: Record<string, string> = {
    USD: 'US Dollar', EUR: 'Euro', GBP: 'British Pound', AED: 'UAE Dirham',
    JPY: 'Japanese Yen', CNY: 'Chinese Yuan', LKR: 'Sri Lankan Rupee',
    INR: 'Indian Rupee', AUD: 'Australian Dollar', CAD: 'Canadian Dollar'
  };

  // --- NEW: Fetch Admin Rate from Firestore ---
  const fetchAdminRate = async () => {
    try {
      const rateDoc = await getDoc(doc(db, "settings", "exchangeRates"));
      if (rateDoc.exists()) {
        setAdminLkrRate(rateDoc.data().USD);
      }
    } catch (error) {
      console.error('Error fetching admin rate:', error);
    }
  };

  const fetchRates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`https://open.er-api.com/v6/latest/${baseCurrency}`);
      const data = await response.json();
      
      // If base is USD and we have an admin rate, we will handle LKR separately in the render
      setRates(data.rates);
      setLastUpdated(new Date().toLocaleTimeString());
      await fetchAdminRate(); // Fetch the firestore rate as well
    } catch (error) {
      console.error('Error fetching rates:', error);
    } finally {
      setLoading(false);
    }
  }, [baseCurrency]);

  useEffect(() => {
    fetchRates();
  }, [fetchRates]);

  const getFlagUrl = (code: string) => {
    let countryCode = code.substring(0, 2).toLowerCase();
    if (code === 'EUR') countryCode = 'eu';
    if (code === 'GBP') countryCode = 'gb';
    return `https://flagcdn.com/w80/${countryCode}.png`;
  };

  return (
    <div>
      <Navbar />
      <div className="exchange-container">
        <div className="max-width-wrapper">
          <header className="header-section">
            <div>
              <div className="brand-group">
                <span className="icon-box"><Globe size={28} /></span>
                <h1 className="header-title">CurrencyX</h1>
              </div>
              <p className="header-subtitle">
                Official Admin & Market Rates • Last updated: {lastUpdated || 'Loading...'}
              </p>
            </div>

            <button onClick={fetchRates} disabled={loading} className="custom-button">
              <RefreshCw size={18} className={loading ? 'spinning' : ''} />
            </button>
          </header>

          {/* ... (Keep your Amount/From Currency sections exactly the same) ... */}
          <section className="glass-panel">
             {/* Amount Input */}
             <div className="input-group">
              <label className="label-text">Amount</label>
              <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="custom-input" />
            </div>
            <div className="swap-icon-container"><ArrowRightLeft size={24} color="#2563eb" /></div>
            {/* From Select */}
            <div className="input-group">
              <label className="label-text">From Currency</label>
              <div className="select-wrapper">
                <img src={getFlagUrl(baseCurrency)} alt="" className="select-flag" />
                <select value={baseCurrency} onChange={(e) => setBaseCurrency(e.target.value)} className="custom-select">
                  {targetCurrencies.map((code) => (
                    <option key={code} value={code}>{code} — {currencyNames[code]}</option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          <div className="results-grid">
            {targetCurrencies.map((code) => {
              if (code === baseCurrency) return null;

              // --- LOGIC: Use Admin Rate if Code is LKR and Base is USD ---
              let rate = rates[code] || 0;
              let isAdminRate = false;

              if (code === 'LKR' && baseCurrency === 'USD' && adminLkrRate) {
                rate = adminLkrRate;
                isAdminRate = true;
              }

              const convertedAmount = (rate * amount).toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2
              });

              return (
                <div key={code} className={`currency-card ${isAdminRate ? 'admin-featured' : ''}`}>
                  <div className="card-header">
                    <div className="currency-info">
                      <img src={getFlagUrl(code)} alt={`${code} flag`} className="flag-icon" />
                      <div>
                        <div className="code-label">{code}</div>
                        <div className="name-text">{currencyNames[code]}</div>
                      </div>
                    </div>
                    {/* Badge shows "OFFICIAL" if it's the admin rate, otherwise "LIVE" */}
                    <div className={isAdminRate ? "official-badge" : "live-badge"}>
                      {isAdminRate ? <ShieldCheck size={12} /> : null}
                      {isAdminRate ? " OFFICIAL" : " LIVE"}
                    </div>
                  </div>

                  <div className="amount-display">
                    {loading ? <div className="loading-shimmer" /> : (
                      <div className="converted-val">{convertedAmount}</div>
                    )}
                    <p className="rate-text">
                      1 {baseCurrency} = {rate.toFixed(4)} {code}
                    </p>
                    {isAdminRate && <p className="official-note">Used for Import Tax Calculations</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ExchangeRatePage;